import React from "react";
import { cn } from "../utils/cn";
import {
  CheckCircleIcon,
  WarningTriangleIcon,
  AlertOctagonIcon,
  LightbulbIcon,
} from "./icons/RecommendationIcons";

type RecommendationType = "success" | "warning" | "error" | "info";

const typeToCardClass: Record<RecommendationType, string> = {
  success: "recommendation-card--success",
  warning: "recommendation-card--warning",
  error: "recommendation-card--error",
  info: "recommendation-card--info",
};

const typeToIconClass: Record<RecommendationType, string> = {
  success: "recommendation-icon--success",
  warning: "recommendation-icon--warning",
  error: "recommendation-icon--error",
  info: "recommendation-icon--info",
};

const typeToSvgIcon: Record<RecommendationType, React.FC> = {
  success: CheckCircleIcon,
  warning: WarningTriangleIcon,
  error: AlertOctagonIcon,
  info: LightbulbIcon,
};

/** Max visible key actions before overflow */
const MAX_VISIBLE_ACTIONS = 2;

interface RecommendationCardProps {
  title: string;
  description: string;
  keyActions?: string[];
  type: RecommendationType;
  /** Handler for "Adjust →" action link. If not provided, no action link is shown. */
  onAction?: () => void;
}

export const RecommendationCard: React.FC<RecommendationCardProps> = React.memo(
  ({ title, description, keyActions, type, onAction }) => {
    const SvgIcon = typeToSvgIcon[type];
    const visibleActions = keyActions?.slice(0, MAX_VISIBLE_ACTIONS);
    const overflowCount = keyActions
      ? keyActions.length - MAX_VISIBLE_ACTIONS
      : 0;

    return (
      <article
        className={cn("recommendation-card", typeToCardClass[type])}
        id={`recommendation-${type}-${title.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
        role="article"
      >
        {/* Header row: icon + title left, action link right */}
        <div className="recommendation-header-row">
          <div className="recommendation-header-left">
            <div className={cn("recommendation-icon", typeToIconClass[type])}>
              <SvgIcon />
            </div>
            <h3 className="recommendation-title">{title}</h3>
          </div>

          {onAction && (
            <button
              type="button"
              className="recommendation-action-link"
              onClick={onAction}
            >
              Adjust →
            </button>
          )}
        </div>

        {/* Content body */}
        <div className="recommendation-content">
          <p className="recommendation-description">{description}</p>

          {visibleActions && visibleActions.length > 0 && (
            <div className="recommendation-actions-wrapper">
              <ul className="recommendation-actions">
                {visibleActions.map((action, index) => (
                  <li key={index} className="recommendation-action-list">
                    <span className="action-bullet">•</span>
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
              {overflowCount > 0 && (
                <p className="recommendation-overflow">
                  and {overflowCount} more{" "}
                  {overflowCount === 1 ? "action" : "actions"}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Spacer pushes content up when card is taller due to min-h */}
        <div className="recommendation-card-spacer" />
      </article>
    );
  }
);

RecommendationCard.displayName = "RecommendationCard";
